/**
 * Bathroom Data Extraction Utilities
 *
 * Extracts wall endpoints, doors, and windows from room data
 * for use by the bathroom analyzer pipeline.
 *
 * Module format: CommonJS
 */

const { apply3DRotationTo2D } = require('../geometry/rotationUtils');

/**
 * Extract wall endpoints from room data.
 *
 * @param {Object} room - Room data with walls array
 * @returns {Array} Array of wall endpoint objects with id, start, end, center, thickness, height, wallDir, perpDir
 */
function extractWallEndpoints(room) {
  const endpoints = [];

  if (!room.walls) return endpoints;
  room.walls.forEach((wall, index) => {
    try {
      const center = {
        x: wall.center.x,
        z: wall.center.z,
      };

      const dimensions = {
        x: wall.dimensions.x,
        y: wall.dimensions.y,
        z: wall.dimensions.z,
      };
      const halfLength = dimensions.x / 2;

      // Wall direction vector in world space
      const wallDirList = apply3DRotationTo2D(wall.rotation);
      const wallDir = { x: wallDirList[0], z: wallDirList[1] };
      const perpDir = { x: -wallDir.z, z: wallDir.x };

      const startX = center.x - wallDir.x * halfLength;
      const startZ = center.z - wallDir.z * halfLength;
      const endX = center.x + wallDir.x * halfLength;
      const endZ = center.z + wallDir.z * halfLength;

      endpoints.push({
        id: wall.id,
        start: { x: startX, z: startZ },
        end: { x: endX, z: endZ },
        center: { x: center.x, z: center.z },
        thickness: dimensions.z,
        height: dimensions.y,
        wallDir: wallDir,
        perpDir: perpDir,
      });
    } catch (err) {
      console.error(`Error processing wall ${index}:`, err);
    }
  });

  return endpoints;
}

/**
 * Extract doors from room data.
 *
 * @param {Object} room - Room data with doors array
 * @param {Map<string, Object>} [structureDoorsById] - Optional structure-side
 *   door lookup keyed by structure-opening id. When provided and a room
 *   copy's `structureOpeningId` resolves, coords / dimensions / doorType /
 *   wallFacesPriorityRoom are read from the structure side (which is
 *   canonical post-P3a). Falls back to room-side reads otherwise.
 * @returns {Array} Array of door objects
 */
function extractDoors(room, structureDoorsById = null) {
  const doors = [];

  if (!room.doors) return doors;

  const wallEndpoints = extractWallEndpoints(room);

  room.doors.forEach((door, index) => {
    try {
      const structureDoor =
        structureDoorsById?.get?.(door.structureOpeningId) || null;
      const source = structureDoor || door;

      const center = {
        x: source.center.x,
        z: source.center.z,
      };

      const dimensions = {
        x: source.dimensions.x,
        y: source.dimensions.y,
        z: source.dimensions.z,
      };
      const halfLength = dimensions.x / 2;

      const parentWallId = door.parentWallIds?.[0];
      const parentWall = wallEndpoints.find((w) => w.id === parentWallId);
      const doorDir = parentWall?.wallDir || { x: 1, z: 0 };
      const perpDir = parentWall?.perpDir || { x: 0, z: 1 };

      const startX = center.x - doorDir.x * halfLength;
      const startZ = center.z - doorDir.z * halfLength;
      const endX = center.x + doorDir.x * halfLength;
      const endZ = center.z + doorDir.z * halfLength;

      const extracted = {
        id: door.id,
        start: { x: startX, z: startZ },
        end: { x: endX, z: endZ },
        center: { x: center.x, z: center.z },
        width: dimensions.x,
        height: dimensions.y,
        thickness: dimensions.z,
        doorDir: doorDir,
        perpDir: perpDir,
        type: 'door',
        parentWallIds: door.parentWallIds,
      };

      // The door's own rotation, carried through so `calculateDoorZone`
      // can resolve `hingeAtStart` in the frame it is defined in. `doorDir`
      // above is the WALL's direction, which runs the other way for ~a third
      // of scanned doors — see `doorJambAxis` in shared-lib/collision.
      if (source.rotation) {
        extracted.rotation = source.rotation;
      }

      const doorType = source.category?.door?.doorType;
      if (doorType) {
        extracted.doorType = doorType;
      }
      if (source.wallFacesPriorityRoom != null) {
        extracted.wallFacesPriorityRoom = source.wallFacesPriorityRoom;
      }

      doors.push(extracted);
    } catch (err) {
      console.error(`Error processing door ${index}:`, err);
    }
  });

  return doors;
}

/**
 * Extract windows from room data.
 *
 * @param {Object} room - Room data with windows array
 * @param {Map<string, Object>} [structureWindowsById] - Optional
 *   structure-side window lookup keyed by structure-opening id. When
 *   provided and a room copy's `structureOpeningId` resolves, coords /
 *   dimensions are read from the structure side (canonical post-P3a).
 *   Falls back to room-side reads otherwise.
 * @returns {Array} Array of window objects
 */
function extractWindows(room, structureWindowsById = null) {
  const windows = [];

  if (!room.windows) return windows;

  const wallEndpoints = extractWallEndpoints(room);

  room.windows.forEach((window, index) => {
    try {
      const structureWindow =
        structureWindowsById?.get?.(window.structureOpeningId) || null;
      const source = structureWindow || window;

      const center = {
        x: source.center?.x !== undefined ? source.center.x : 0,
        y: source.center?.y !== undefined ? source.center.y : 1.5,
        z: source.center?.z !== undefined ? source.center.z : 0,
      };

      const dimensions = {
        x:
          source.dimensions?.x !== undefined
            ? source.dimensions.x
            : source.dimensions?.width || 1,
        y:
          source.dimensions?.y !== undefined
            ? source.dimensions.y
            : source.dimensions?.height || 1,
        z:
          source.dimensions?.z !== undefined
            ? source.dimensions.z
            : source.dimensions?.depth || source.dimensions?.thickness || 0.1,
      };
      const halfWidth = dimensions.x / 2;

      const parentWallId = window.parentWallIds?.[0];
      const parentWall = wallEndpoints.find((w) => w.id === parentWallId);
      const windowDir = parentWall?.wallDir || { x: 1, z: 0 };
      const perpDir = parentWall?.perpDir || { x: 0, z: 1 };

      const startX = center.x - windowDir.x * halfWidth;
      const startZ = center.z - windowDir.z * halfWidth;
      const endX = center.x + windowDir.x * halfWidth;
      const endZ = center.z + windowDir.z * halfWidth;

      // Repair a window whose height looks unusable, by seating it at a
      // typical sill. BOTH the test and the replacement are measured FROM THE
      // ROOM'S FLOOR, because `center.y` is world-y and a room's floor is
      // hardly ever at y = 0 — 1,325 of 1,465 rooms on dev are not.
      //
      // Comparing the absolute `center.y` against 0.5 meant a perfectly good
      // window in a room whose floor sits below zero was treated as broken and
      // overwritten with a fabricated sill. Measured across the corpus: 546 of
      // 680 windows tripped the old absolute test and ALL 546 were fine
      // relative to their own floor — it never once fired correctly, it only
      // ever replaced real geometry.
      //
      // The damage shows up wherever `windowTop`/`windowBottom` are read
      // alongside a real floor Y: a curtain 4.15m tall in a 2.51m room
      // (`planWindowDecor`), and a windowsill plant seated at the wrong height
      // (`planPlants`). Those are the only two consumers.
      const roomFloorY = room?.floors?.[0]?.center?.y ?? 0;
      const TYPICAL_WINDOW_BOTTOM_METERS = 0.9;
      let adjustedCenterY = center.y;
      if (center.y - roomFloorY < 0.5) {
        adjustedCenterY =
          roomFloorY + TYPICAL_WINDOW_BOTTOM_METERS + dimensions.y / 2;
      }

      const windowBottom = adjustedCenterY - dimensions.y / 2;
      const windowTop = adjustedCenterY + dimensions.y / 2;

      windows.push({
        id: window.id,
        start: { x: startX, z: startZ },
        end: { x: endX, z: endZ },
        center: { x: center.x, y: center.y, z: center.z },
        width: dimensions.x,
        height: dimensions.y,
        thickness: dimensions.z,
        windowDir: windowDir,
        perpDir: perpDir,
        type: 'window',
        parentWallIds: window.parentWallIds,
        windowBottom,
        windowTop,
      });
    } catch (err) {
      console.error(`Error processing window ${index}:`, err);
    }
  });

  return windows;
}

module.exports = {
  extractWallEndpoints,
  extractDoors,
  extractWindows,
};
